import React from 'react';
import {
  Box,
  Button,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { DeleteOutlined } from '@mui/icons-material';

import DataTableModule from '@/__core/layout/module/DataTableModule';
import AddButtonAutocomplete from '@/__core/hocs/AddButtonAutocomplete';

import { InventoryIssueFormLine } from '../types';

const TABLE_MIN_WIDTH = 748;
const ROW_HEIGHT = 44;

interface StockBalanceSnapshot {
  on_hand_qty: number;
  allocated_qty: number;
  available_qty: number;
  uom_name?: string;
}

const editableCellSx = {
  minWidth: 0,
  '& .MuiInputBase-root': {
    height: '30px !important',
    minHeight: '30px !important',
    maxHeight: '30px !important',
    backgroundColor: 'transparent',
    border: 'none !important',
    borderBottom: '1px solid #d1d5db',
    borderRadius: '0 !important',
    '&:hover': {
      borderBottom: '1px solid #9ca3af',
    },
    '&.Mui-focused': {
      borderBottom: '1px solid #3b82f6',
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none !important',
  },
  '& .MuiInputBase-input': {
    padding: '6px 0 !important',
    fontSize: '0.875rem',
    color: '#374151 !important',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
};

const compactCellSx = {
  height: ROW_HEIGHT,
  py: 0.5,
  verticalAlign: 'middle',
  overflow: 'hidden',
};

interface IssueLinesTableProps {
  lines: InventoryIssueFormLine[];
  itemOptions: Array<{ id: string; name: string; code: string; unit_id?: string; unit_name?: string }>;
  uomOptionsByLineId?: Record<number, Array<{ id: string; name: string }>>;
  itemLoading?: boolean;
  stockByLineId?: Record<number, StockBalanceSnapshot | null>;
  stockLoadingByLineId?: Record<number, boolean>;
  stockErrorByLineId?: Record<number, boolean>;
  onChangeLine: (lineId: number, updates: Partial<InventoryIssueFormLine>) => void;
  onAddLine: () => void;
  onRemoveLine: (lineId: number) => void;
  disabled?: boolean;
}

export default function IssueLinesTable({
  lines,
  itemOptions,
  uomOptionsByLineId = {},
  itemLoading = false,
  stockByLineId = {},
  stockLoadingByLineId = {},
  stockErrorByLineId = {},
  onChangeLine,
  onAddLine,
  onRemoveLine,
  disabled = false,
}: IssueLinesTableProps) {
  const safeLines = lines || [];
  const [manualItemOptions, setManualItemOptions] = React.useState<Array<{ id: string; name: string; code: string }>>([]);

  const effectiveItemOptions = React.useMemo(() => {
    const merged = [...itemOptions, ...manualItemOptions];
    const unique = new Map<string, { id: string; name: string; code: string }>();
    merged.forEach((item) => {
      if (!unique.has(item.id)) {
        unique.set(item.id, item);
      }
    });
    return Array.from(unique.values());
  }, [itemOptions, manualItemOptions]);

  const formatQty = (value: number | string | null | undefined) => {
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) {
      return '0';
    }
    return numericValue.toLocaleString('vi-VN', { maximumFractionDigits: 2 });
  };

  const renderStockCell = (line: InventoryIssueFormLine, mode: 'on_hand' | 'allocated') => {
    const selectedItem = effectiveItemOptions.find((item) => item.id === line.item_id) || null;
    const itemCode = selectedItem?.code || line.item_code || '';
    const stockSnapshot = stockByLineId[line.id] || null;

    const qty = !itemCode
      ? 0
      : mode === 'on_hand'
        ? Number(stockSnapshot?.on_hand_qty) || 0
        : Number(stockSnapshot?.allocated_qty) || 0;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '30px' }}>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: '#374151', lineHeight: 1 }}>
          {formatQty(qty)}
        </Typography>
      </Box>
    );
  };

  const tableRows = React.useMemo(
    () => safeLines.length > 0
      ? safeLines.map((line, index) => ({ ...line, __index: index }))
      : [{ __rowId: 'empty-issue-lines', __fullWidthRow: true, __empty: true }],
    [safeLines],
  );

  const columns = [
    {
      id: '__index',
      label: '#',
      width: '40px',
      align: 'center' as const,
      render: (_value: unknown, row: any) => {
        if (row.__empty) {
          return (
            <Typography sx={{ textAlign: 'center', py: 3, color: '#9ca3af', fontSize: '0.875rem' }}>
              Chưa có dòng vật tư. Nhấn &quot;Thêm item&quot; để bắt đầu.
            </Typography>
          );
        }
        return (
          <Typography sx={{ color: '#6b7280', fontSize: '0.75rem', fontWeight: 500 }}>
            {Number(row.__index) + 1}
          </Typography>
        );
      },
    },
    {
      id: 'item',
      label: 'Vật tư',
      width: '200px',
      render: (_value: unknown, row: InventoryIssueFormLine) => {
        const selectedItem = effectiveItemOptions.find((item) => item.id === row.item_id) || null;
        return (
          <AddButtonAutocomplete
            options={effectiveItemOptions}
            value={selectedItem || (row.item_id ? { id: row.item_id, name: row.item_name, code: row.item_code } : null)}
            getOptionLabel={(option) => option?.code ? `${option.code} - ${option.name}` : option?.name || ''}
            renderOption={(props, option) => (
              <Box component='li' {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75 }}>
                <Typography sx={{ fontSize: '0.72rem', color: '#6b7280', fontFamily: '"Roboto Mono", monospace' }}>{option.code}</Typography>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#111827' }}>{option.name}</Typography>
              </Box>
            )}
            onChange={(_event, newValue) => {
              const nextItem = newValue as { id: string; name: string; code: string; unit_id?: string; unit_name?: string } | null;
              onChangeLine(row.id, {
                item_id: nextItem?.id || '',
                item_code: nextItem?.code || '',
                item_name: nextItem?.name || '',
                uom_id: nextItem?.unit_id || '',
                uom_name: nextItem?.unit_name || '',
              });
            }}
            onAddNew={async ({ name }) => {
              const created = { id: `manual-item-${Date.now()}`, name: name.trim(), code: '' };
              setManualItemOptions((current) => [...current, created]);
              return created;
            }}
            placeholder='Chọn vật tư'
            noOptionsText='Không tìm thấy vật tư'
            loading={itemLoading}
            disableClearable={Boolean(row.item_id)}
            disabled={disabled}
            fullWidth
            sx={editableCellSx}
          />
        );
      },
    },
    {
      id: 'on_hand',
      label: 'Tồn kho',
      width: '120px',
      align: 'center' as const,
      render: (_value: unknown, row: InventoryIssueFormLine) => renderStockCell(row, 'on_hand'),
    },
    {
      id: 'allocated',
      label: 'Đã phân bổ',
      width: '120px',
      align: 'center' as const,
      render: (_value: unknown, row: InventoryIssueFormLine) => renderStockCell(row, 'allocated'),
    },
    {
      id: 'uom',
      label: 'ĐVT',
      width: '100px',
      render: (_value: unknown, row: InventoryIssueFormLine) => {
        const lineUomOptions = uomOptionsByLineId[row.id] || [];
        const selectedUom = lineUomOptions.find((option) => option.id === row.uom_id) || null;
        return (
          <AddButtonAutocomplete
            options={lineUomOptions}
            value={selectedUom || (row.uom_id ? { id: row.uom_id, name: row.uom_name } : null)}
            onChange={(_event, newValue) => {
              const nextUom = newValue as { id: string; name: string } | null;
              onChangeLine(row.id, {
                uom_id: nextUom?.id || '',
                uom_name: nextUom?.name || '',
              });
            }}
            placeholder='Chọn ĐVT'
            noOptionsText='Không tìm thấy ĐVT'
            disabled={disabled}
            canAddNew={false}
            fullWidth
            sx={editableCellSx}
          />
        );
      },
    },
    {
      id: 'issued_qty',
      label: 'SL xuất',
      width: '120px',
      align: 'right' as const,
      render: (_value: unknown, row: InventoryIssueFormLine) => (
        <TextField
          size='small'
          type='number'
          value={row.issued_qty}
          onChange={(event) => onChangeLine(row.id, { issued_qty: Number(event.target.value) || 0 })}
          inputProps={{ min: 0, step: 0.001, style: { textAlign: 'right' } }}
          disabled={disabled}
          fullWidth
          sx={editableCellSx}
        />
      ),
    },
    {
      id: 'actions',
      label: '',
      width: '48px',
      align: 'center' as const,
      render: (_value: unknown, row: InventoryIssueFormLine) => (
        <IconButton
          size='small'
          onClick={() => onRemoveLine(row.id)}
          disabled={disabled}
          sx={{ color: '#ef4444' }}
        >
          <DeleteOutlined fontSize='small' />
        </IconButton>
      ),
    },
  ];

  return (
    <Paper
      sx={{
        padding: 0,
        borderRadius: 1,
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
        backgroundColor: 'white',
        width: '100%',
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      <DataTableModule
        data={tableRows}
        columns={columns}
        leftFilterWidth={0}
        reserveLeftFilterSpace={false}
        showCheckbox={false}
        fullWidth
        tableLayout='fixed'
        tableContainerSx={{
          maxWidth: '100%',
          overflowX: 'auto',
          overflowY: 'hidden',
          scrollbarGutter: 'stable',
          overflowAnchor: 'none',
          cursor: 'default',
          '&::-webkit-scrollbar': { height: '8px' },
          '&::-webkit-scrollbar-track': { backgroundColor: '#f1f1f1', borderRadius: '4px' },
          '&::-webkit-scrollbar-thumb': { backgroundColor: '#c1c1c1', borderRadius: '4px', '&:hover': { backgroundColor: '#a8a8a8' } },
        }}
        tableSx={{
          minWidth: TABLE_MIN_WIDTH,
          '& th, & td': compactCellSx,
        }}
        onRowSelect={() => undefined}
        onSelectAll={() => undefined}
        getRowId={(row) => String(row.__rowId || row.id)}
        isRowSelected={() => false}
        areAllRowsSelected={false}
        areSomeRowsSelected={false}
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, pr: 1, gap: 1 }}>
        <Button
          variant='contained'
          size='small'
          onClick={onAddLine}
          disabled={disabled}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Thêm item
        </Button>
      </Box>
    </Paper>
  );
}
